import React, { useEffect, useState, useMemo } from 'react';
import {
    PencilSquareIcon, PlusIcon, XMarkIcon,
    CheckCircleIcon, TrashIcon,
} from '@heroicons/react/24/outline';
import { useCakeOptions, useCakeSizes } from '../../hooks/useOrders';
import type { CakeOption, CakeOptionPayload, CakeOptionPriceUpsert } from '../../types';

// ── Types ──────────────────────────────────────────────────────────────────────

type OptionForm = {
    label: string;
    category: string;
    flat_rate: boolean;
    sort_order: string;
};

// ── Constants ─────────────────────────────────────────────────────────────────

const FIELD = 'w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ee2b74]';
const emptyForm = (): OptionForm => ({ label: '', category: '', flat_rate: false, sort_order: '0' });

// ── Component ─────────────────────────────────────────────────────────────────

export default function CakeOptionsTab() {
    const { options, prices, loading, fetchAll, createOption, updateOption, toggleActive, deleteOption } = useCakeOptions();
    const { sizes, fetchSizes } = useCakeSizes();

    // Derive categories dynamically from DB values
    const categories = useMemo(() => {
        const set = new Set(options.map(o => o.category).filter(Boolean));
        return Array.from(set).sort();
    }, [options]);

    const [activeCategory, setActiveCategory] = useState('');
    const [isModalOpen,    setIsModalOpen]    = useState(false);
    const [editingOption,  setEditingOption]  = useState<CakeOption | null>(null);
    const [form,           setForm]           = useState<OptionForm>(emptyForm());
    const [newCategory,    setNewCategory]    = useState('');
    const [priceInputs,    setPriceInputs]    = useState<Record<string, string>>({});
    const [saving,         setSaving]         = useState(false);
    const [success,        setSuccess]        = useState('');
    const [togglingId,     setTogglingId]     = useState<string | null>(null);
    const [deletingId,     setDeletingId]     = useState<string | null>(null);

    useEffect(() => {
        void Promise.all([fetchAll(), fetchSizes()]);
    }, [fetchAll, fetchSizes]);

    // Sync activeCategory once categories load
    useEffect(() => {
        if (activeCategory === '' && categories.length > 0) {
            setActiveCategory(categories[0]);
        }
    }, [categories, activeCategory]);

    const priceFor = (optionId: string, sizeId: string | null) =>
        prices.find(p => p.option_id === optionId && p.size_id === sizeId);

    const openCreate = () => {
        setEditingOption(null);
        setForm({ ...emptyForm(), category: activeCategory });
        setNewCategory('');
        setPriceInputs({});
        setSuccess('');
        setIsModalOpen(true);
    };

    const openEdit = (opt: CakeOption) => {
        setEditingOption(opt);
        setForm({ label: opt.label, category: opt.category, flat_rate: opt.flat_rate, sort_order: String(opt.sort_order) });
        setNewCategory('');
        const inputs: Record<string, string> = {};
        if (opt.flat_rate) {
            const p = priceFor(opt.id, null);
            inputs['flat'] = p ? (p.price / 100).toFixed(2) : '';
        } else {
            sizes.forEach(s => {
                const p = priceFor(opt.id, s.id);
                inputs[s.id] = p ? (p.price / 100).toFixed(2) : '';
            });
        }
        setPriceInputs(inputs);
        setSuccess('');
        setIsModalOpen(true);
    };

    const closeModal = () => { setIsModalOpen(false); setSuccess(''); };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            // Resolve category — use newCategory text if "Add new" was selected
            const resolvedCategory = form.category === '__new__'
                ? newCategory.trim().toLowerCase().replace(/\s+/g, '_')
                : form.category;

            if (!resolvedCategory) return;

            const payload: CakeOptionPayload = {
                label:      form.label.trim(),
                category:   resolvedCategory,
                flat_rate:  form.flat_rate,
                sort_order: parseInt(form.sort_order) || 0,
            };

            let ok: boolean;

            if (editingOption) {
                const priceList: CakeOptionPriceUpsert[] = form.flat_rate
                    ? [{ size_id: null, price: Math.round(parseFloat(priceInputs['flat'] || '0') * 100) }]
                    : sizes.map(size => ({
                        size_id: size.id,
                        price:   Math.round(parseFloat(priceInputs[size.id] || '0') * 100),
                    }));

                ok = await updateOption(editingOption.id, payload, priceList);
            } else {
                const priceList: CakeOptionPriceUpsert[] = form.flat_rate
                    ? [{ size_id: null, price: Math.round(parseFloat(priceInputs['flat'] || '0') * 100) }]
                    : sizes.map(size => ({
                        size_id: size.id,
                        price:   Math.round(parseFloat(priceInputs[size.id] || '0') * 100),
                    }));

                ok = await createOption(payload, priceList);
            }

            if (ok) {
                setSuccess(editingOption ? 'Option updated!' : 'Option created!');
                setActiveCategory(resolvedCategory);
                setTimeout(closeModal, 1500);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (opt: CakeOption) => {
        setTogglingId(opt.id);
        await toggleActive(opt.id, !opt.active);
        setTogglingId(null);
    };

    const handleDelete = async (opt: CakeOption) => {
        if (!window.confirm(`Delete "${opt.label}"? This cannot be undone.`)) return;
        setDeletingId(opt.id);
        const ok = await deleteOption(opt.id);
        if (!ok) alert('Delete failed.');
        setDeletingId(null);
    };

    const filteredOptions = options.filter(o => o.category === activeCategory);

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <p className="text-sm text-stone-500">
                    {loading ? 'Loading…' : `${filteredOptions.length} option${filteredOptions.length !== 1 ? 's' : ''} in ${activeCategory.replace(/_/g, ' ')}`}
                </p>
                <button onClick={openCreate}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#ee2b74] hover:bg-[#d4245f] text-white text-sm font-medium transition-colors">
                    <PlusIcon className="h-4 w-4" />
                    Add Option
                </button>
            </div>

            {/* Category pills — derived from DB */}
            <div className="flex flex-wrap gap-2">
                {categories.map(cat => {
                    const count    = options.filter(o => o.category === cat).length;
                    const isActive = activeCategory === cat;
                    return (
                        <button key={cat} onClick={() => setActiveCategory(cat)}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors capitalize ${
                                    isActive
                                        ? 'bg-[#ee2b74] border-[#ee2b74] text-white'
                                        : 'bg-white border-stone-200 text-stone-600 hover:border-stone-300'
                                }`}>
                            {cat.replace(/_/g, ' ')} <span className="opacity-70">({count})</span>
                        </button>
                    );
                })}
                {categories.length === 0 && !loading && (
                    <span className="text-xs text-stone-400 italic">No categories yet — add your first option to create one.</span>
                )}
            </div>

            {/* Table */}
            {loading ? (
                <div className="animate-pulse space-y-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="h-12 rounded-xl bg-stone-100" />
                    ))}
                </div>
            ) : filteredOptions.length === 0 ? (
                <div className="py-12 text-center text-sm text-stone-400">
                    No {activeCategory.replace(/_/g, ' ')} options yet.
                </div>
            ) : (
                <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b border-stone-200 bg-stone-50">
                            <tr>
                                {['Option', 'Pricing', 'Sort', 'Status', ''].map(h => (
                                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">{h}</th>
                                ))}
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                            {filteredOptions.map(opt => {
                                const flatPrice  = priceFor(opt.id, null);
                                const sizePrices = sizes.map(s => ({ size: s, price: priceFor(opt.id, s.id) }));
                                return (
                                    <tr key={opt.id} className={`hover:bg-stone-50 transition-colors ${!opt.active ? 'opacity-50' : ''}`}>
                                        <td className="px-4 py-3 font-medium text-stone-900">{opt.label}</td>
                                        <td className="px-4 py-3 text-stone-500">
                                            {opt.flat_rate ? (
                                                <span>
                                                    {flatPrice ? `R${(flatPrice.price / 100).toFixed(2)}` : '—'}
                                                    <span className="text-xs text-stone-400 ml-1">(flat)</span>
                                                </span>
                                            ) : (
                                                <span className="text-xs">
                                                    {sizePrices.map(({ size, price }) => (
                                                        <span key={size.id} className="mr-2">
                                                            {size.label}: {price ? `R${(price.price / 100).toFixed(2)}` : '—'}
                                                        </span>
                                                    ))}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-stone-400">{opt.sort_order}</td>
                                        <td className="px-4 py-3">
                                            <button onClick={() => void handleToggle(opt)}
                                                    disabled={togglingId === opt.id}
                                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${opt.active ? 'bg-[#ee2b74]' : 'bg-stone-200'}`}>
                                                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${opt.active ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                                            </button>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1 justify-end">
                                                <button onClick={() => openEdit(opt)}
                                                        className="p-1.5 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors">
                                                    <PencilSquareIcon className="h-4 w-4" />
                                                </button>
                                                <button onClick={() => void handleDelete(opt)}
                                                        disabled={deletingId === opt.id}
                                                        className="p-1.5 rounded-lg text-stone-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50">
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-[1px]">
                    <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl ring-1 ring-stone-200 overflow-hidden max-h-[90vh] overflow-y-auto">
                        <div className="h-1 w-full bg-[#ee2b74] sticky top-0" />
                        <div className="p-6 space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-stone-900">
                                        {editingOption ? 'Edit Option' : 'Add Cake Option'}
                                    </h3>
                                    <p className="text-xs text-stone-500 mt-0.5">Configure the option and its pricing</p>
                                </div>
                                <button onClick={closeModal}
                                        className="rounded-lg p-1 text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors">
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>

                            {success && (
                                <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 text-emerald-700 text-sm">
                                    <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />{success}
                                </div>
                            )}

                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1.5">Label</label>
                                    <input type="text" required value={form.label}
                                           onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
                                           placeholder="e.g. Chocolate Ganache"
                                           className={FIELD} />
                                </div>

                                {/* Category — dynamic dropdown with "Add new" option */}
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1.5">Category</label>
                                    <select
                                        value={form.category}
                                        onChange={e => {
                                            setForm(p => ({ ...p, category: e.target.value }));
                                            if (e.target.value !== '__new__') setNewCategory('');
                                        }}
                                        className={FIELD}
                                    >
                                        <option value="" disabled>Select a category…</option>
                                        {categories.map(c => (
                                            <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                                        ))}
                                        <option value="__new__">+ Add new category…</option>
                                    </select>
                                    {form.category === '__new__' && (
                                        <input
                                            type="text"
                                            placeholder="e.g. toppings"
                                            value={newCategory}
                                            onChange={e => setNewCategory(e.target.value)}
                                            className={`${FIELD} mt-2`}
                                            autoFocus
                                        />
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1.5">Sort Order</label>
                                    <input type="number" min="0" value={form.sort_order}
                                           onChange={e => setForm(p => ({ ...p, sort_order: e.target.value }))}
                                           className={FIELD} />
                                </div>

                                {/* Flat rate toggle */}
                                <div className="flex items-center gap-3">
                                    <button type="button"
                                            onClick={() => setForm(p => ({ ...p, flat_rate: !p.flat_rate }))}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${form.flat_rate ? 'bg-[#ee2b74]' : 'bg-stone-200'}`}>
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.flat_rate ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                    <div>
                                        <p className="text-sm font-medium text-stone-700">Flat rate pricing</p>
                                        <p className="text-xs text-stone-400">Same price regardless of cake size.</p>
                                    </div>
                                </div>

                                {/* Price inputs */}
                                <div className="rounded-lg border border-stone-200 p-4 space-y-3">
                                    <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
                                        {form.flat_rate ? 'Add-on Price (R)' : 'Add-on Price per Size (R)'}
                                    </p>
                                    {form.flat_rate ? (
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">R</span>
                                            <input type="number" step="0.01" min="0"
                                                   value={priceInputs['flat'] ?? ''}
                                                   onChange={e => setPriceInputs(p => ({ ...p, flat: e.target.value }))}
                                                   placeholder="0.00"
                                                   className={`${FIELD} pl-8`} />
                                        </div>
                                    ) : sizes.map(size => (
                                        <div key={size.id} className="flex items-center gap-3">
                                            <span className="text-sm text-stone-600 w-24 flex-shrink-0">{size.label}</span>
                                            <div className="relative flex-1">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">R</span>
                                                <input type="number" step="0.01" min="0"
                                                       value={priceInputs[size.id] ?? ''}
                                                       onChange={e => setPriceInputs(p => ({ ...p, [size.id]: e.target.value }))}
                                                       placeholder="0.00"
                                                       className={`${FIELD} pl-8`} />
                                            </div>
                                        </div>
                                    ))}
                                    <p className="text-xs text-stone-400">Set to R0.00 for included options (no extra charge)</p>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
                                    <button type="button" onClick={closeModal} disabled={saving}
                                            className="px-4 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50 disabled:opacity-50 transition-colors">
                                        Cancel
                                    </button>
                                    <button type="submit"
                                            disabled={
                                                saving || !!success ||
                                                (form.category === '__new__' && !newCategory.trim()) ||
                                                !form.category
                                            }
                                            className="px-4 py-2 rounded-lg bg-[#ee2b74] hover:bg-[#d4245f] text-white text-sm font-medium disabled:opacity-50 transition-colors flex items-center gap-2">
                                        {saving ? 'Saving…'
                                            : success ? <><CheckCircleIcon className="h-4 w-4" /> Saved!</>
                                                : editingOption ? 'Save Changes' : 'Add Option'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}